// src/components/CycleSettings/CycleSettings.tsx

import React, { useMemo, useState } from 'react';
import ModalFullScreen from '../ModalFullScreen/ModalFullScreen';
import { useCycleSettingsData } from './hooks/useCycleSettingsData';
import ModeSettingsPanel from './components/ModeSettingsPanel';
import ChannelsTable from './components/ChannelsTable';
import PendingSettingsBanner from './components/PendingSettingsBanner';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch, RootState } from '../../store/store';
import CycleSettingsEnum from '../../enums/cycle-setting.enum';
import { CycleSettingAction } from '../../enums/action';
import getMeasurementPreference from '../../utils/getMeasurementPreference';
import CycleMeasurement from '../../enums/cycle-measurement';
import { IDbCycleSettings } from '../../store/models/cycles';
import { IRecipeStepEditable, StepMode } from '../../types/recipes-types';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../utils/temperature-unit-conversion';

interface ComponentProps {
  isVisible: boolean;
  onClose: () => void;
  cycleId: string;
  cycleName: string;
}

const CycleSettings: React.FC<ComponentProps> = ({ isVisible, onClose, cycleId, cycleName }) => {
  const dispatch = useDispatch<Dispatch>();
  const { contract, currentLocation } = useSelector((state: RootState) => ({
    contract: state.contracts.contract,
    currentLocation: state.locations.currentLocation,
  }));
  const [isApplyingPending, setIsApplyingPending] = useState(false);

  const {
    localMode,
    setLocalMode,
    isLoading,
    isShowContent,
    isShowChannelTable,
    cycleDbSettings,
    channelsSettings,
    refreshData,
  } = useCycleSettingsData(cycleId);

  const hasPendingSettings = useMemo(() => cycleDbSettings?.pendingCycleSettings != null, [cycleDbSettings]);
  const measurement = useMemo(() => getMeasurementPreference(contract, currentLocation), [contract, currentLocation]);

  const normalizeRecipeStepsForMeasurement = (steps: IRecipeStepEditable[] = [], units: CycleMeasurement) =>
    steps.map((step, index) => {
      const sourceUnits =
        step.units || (step.isCelsius === false ? CycleMeasurement.Fahrenheit : CycleMeasurement.Celsius);
      const shouldConvertValues = sourceUnits !== units && step.mode === StepMode.Rate;
      const normalizedStep = {
        ...step,
        order: step.order ?? index,
        heatingRate: shouldConvertValues
          ? (convertTemperatureDelta(step.heatingRate, sourceUnits, units) as number)
          : step.heatingRate,
        heatingLimit: shouldConvertValues
          ? (convertAbsoluteTemperature(step.heatingLimit, sourceUnits, units) as number)
          : step.heatingLimit,
        isCelsius: units === CycleMeasurement.Celsius,
        units,
      };

      return normalizedStep;
    });

  const normalizePendingRecipeForDb = (pendingSettings: IDbCycleSettings, units: CycleMeasurement) => {
    if (units === CycleMeasurement.Celsius) {
      return {
        ...pendingSettings,
        steps: normalizeRecipeStepsForMeasurement(pendingSettings.steps, units),
      };
    }

    return {
      ...pendingSettings,
      rateUpperTolerance: convertTemperatureDelta(
        pendingSettings.rateUpperTolerance,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      rateLowerTolerance: convertTemperatureDelta(
        pendingSettings.rateLowerTolerance,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      holdUpperTolerance: convertTemperatureDelta(
        pendingSettings.holdUpperTolerance,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      holdLowerTolerance: convertTemperatureDelta(
        pendingSettings.holdLowerTolerance,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      rate: convertTemperatureDelta(
        pendingSettings.rate,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      finalTemp: convertAbsoluteTemperature(
        pendingSettings.finalTemp,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      soakUpper: convertTemperatureDelta(
        pendingSettings.soakUpper,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      soakLower: convertTemperatureDelta(
        pendingSettings.soakLower,
        CycleMeasurement.Fahrenheit,
        CycleMeasurement.Celsius,
      ) as number,
      steps: normalizeRecipeStepsForMeasurement(pendingSettings.steps, units),
    };
  };

  const handleApplyPendingSettings = async () => {
    if (!cycleDbSettings?.pendingCycleSettings) return;

    setIsApplyingPending(true);
    try {
      const pendingSettings = normalizePendingRecipeForDb(cycleDbSettings.pendingCycleSettings, measurement);

      const { steps, ...settingsWithoutSteps } = pendingSettings;
      await dispatch.cycles.updateDbSettings({
        id: cycleId,
        payload: { cycleDbSettings: settingsWithoutSteps, steps },
      });

      if (pendingSettings.mode === CycleSettingsEnum.PostWeld) {
        const wholeRecipe = {
          name: pendingSettings.name,
          rateUpperTolerance: pendingSettings.rateUpperTolerance,
          rateLowerTolerance: pendingSettings.rateLowerTolerance,
          holdUpperTolerance: pendingSettings.holdUpperTolerance,
          holdLowerTolerance: pendingSettings.holdLowerTolerance,
          steps: pendingSettings.steps,
        };
        const stringData = JSON.stringify(wholeRecipe);
        await dispatch.cycles.updateSettings({
          id: cycleId,
          payload: { action: CycleSettingAction.SET_CYCLE_UPDATE_WHOLE_RECIPE, value: stringData },
        });
      } else if (pendingSettings.mode === CycleSettingsEnum.PreHeat) {
        const wholeRecipe = {
          holdingTemp: pendingSettings.finalTemp,
          lowerHoldTolerance: pendingSettings.soakLower,
          upperHoldTolerance: pendingSettings.soakUpper,
          rate: pendingSettings.rate,
        };
        const stringData = JSON.stringify(wholeRecipe);
        await dispatch.cycles.updateSettings({
          id: cycleId,
          payload: { action: CycleSettingAction.SET_CYCLE_UPDATE_WHOLE_PRE_HEAT, value: stringData },
        });
      } else if (pendingSettings.mode === CycleSettingsEnum.PercentageTimer) {
        await dispatch.cycles.updateSettings({
          id: cycleId,
          payload: {
            action: CycleSettingAction.SET_PERCENTAGE_TIMER,
            value: String(pendingSettings.percentageTimer),
          },
        });
      }

      setLocalMode(pendingSettings.mode);

      // Clear old state and refresh data to show updated settings
      dispatch.cycles.clearDbSettings();
      dispatch.cycles.clearSettingsMode();
      dispatch.cycles.clearCycleIoTChannelsSettings();

      // Small delay to ensure server has processed the changes
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Fetch fresh data
      await refreshData();
    } catch (error) {
      console.error('Failed to apply pending settings:', error);
    } finally {
      setIsApplyingPending(false);
    }
  };

  if (!isVisible) return null;

  return (
    <ModalFullScreen
      modalTitle={`Cycle settings - ${cycleName}`}
      // ${isActiveCycle ? ' (Active)' : ''}
      modalSubTitle="Choose mode and modify settings"
      isVisible={isVisible}
      onClose={onClose}
      showAcceptBtn={false}
      showCancelBtn={false}
    >
      <div className="c-cycle-settings relative flex min-h-full w-full flex-col overflow-auto">
        {hasPendingSettings && cycleDbSettings?.pendingCycleSettings && (
          <div className="px-6 pt-6">
            <PendingSettingsBanner
              pendingSettings={cycleDbSettings.pendingCycleSettings}
              onApplyAll={handleApplyPendingSettings}
              isLoading={isApplyingPending}
            />
          </div>
        )}
        <div className="flex flex-col-reverse justify-center 1.5xl:flex-row">
          <ModeSettingsPanel
            isLoading={isLoading}
            isShowContent={isShowContent}
            localMode={localMode}
            cycleId={cycleId}
            cycleDbSettings={cycleDbSettings}
            setLocalMode={setLocalMode}
          />
          {isShowChannelTable && (
            <ChannelsTable channelsSettings={channelsSettings} cycleDbSettings={cycleDbSettings} measurement={measurement} />
          )}
        </div>
      </div>
    </ModalFullScreen>
  );
};

export default CycleSettings;
